import { useEffect, useState } from "react";
import { Loader2, Sparkles, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const EMPTY = { lead_name: "", lead_phone: "", lead_email: "", lead_city: "", need: "", category_id: "", category_source: "manual", consent: false };

const Field = ({ label, id, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-sm text-[#12201A]">{label}</Label>
    {children}
  </div>
);

export const ReferralForm = ({ onCreated }) => {
  const [cats, setCats] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const suggest = async () => {
    if (f.need.trim().length < 5) return toast.error("Descrivi prima l'esigenza (almeno qualche parola)");
    setAiLoading(true);
    setAi(null);
    try {
      const { data } = await api.post("/referrals/suggest-category", { need: f.need });
      setAi(data);
      if (data.category_id) setF((p) => ({ ...p, category_id: data.category_id, category_source: "ai" }));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAiLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.lead_phone.trim() && !f.lead_email.trim()) return toast.error("Inserisci almeno telefono o email");
    if (!f.category_id) return toast.error("Scegli la tipologia");
    if (!f.consent) return toast.error("Serve il consenso della persona segnalata");
    setSaving(true);
    try {
      const { data } = await api.post("/referrals", f);
      toast.success("Segnalazione inviata! La verificheremo a breve.");
      setF(EMPTY);
      setAi(null);
      onCreated?.(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="referral-form">
      <Field label="Nome e cognome della persona *" id="lead_name">
        <Input id="lead_name" required className="h-11" value={f.lead_name} onChange={set("lead_name")} data-testid="input-referral-name" />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Telefono" id="lead_phone">
          <Input id="lead_phone" type="tel" className="h-11" value={f.lead_phone} onChange={set("lead_phone")} data-testid="input-referral-phone" />
        </Field>
        <Field label="Email" id="lead_email">
          <Input id="lead_email" type="email" className="h-11" value={f.lead_email} onChange={set("lead_email")} data-testid="input-referral-email" />
        </Field>
      </div>
      <p className="-mt-2 text-xs text-[#75867E]">Almeno uno tra telefono ed email.</p>
      <Field label="Città *" id="lead_city">
        <Input id="lead_city" required className="h-11" value={f.lead_city} onChange={set("lead_city")} data-testid="input-referral-city" />
      </Field>
      <Field label="Di cosa ha bisogno? *" id="need">
        <Textarea id="need" required rows={4} value={f.need} onChange={set("need")} placeholder="Es. vorrebbe installare pannelli fotovoltaici sulla sua villetta" data-testid="textarea-referral-need" />
      </Field>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-sm">Tipologia *</Label>
          <Button type="button" variant="ghost" size="sm" onClick={suggest} disabled={aiLoading} className="h-9 rounded-full text-[#8A6A00] hover:bg-[#F4C20D]/10 hover:text-[#8A6A00]" data-testid="btn-ai-suggest-category">
            {aiLoading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />} Suggerisci con AI
          </Button>
        </div>
        <Select value={f.category_id} onValueChange={(v) => setF({ ...f, category_id: v, category_source: "manual" })}>
          <SelectTrigger className="h-11" data-testid="select-referral-category"><SelectValue placeholder="Scegli una tipologia" /></SelectTrigger>
          <SelectContent>
            {cats.map((c) => <SelectItem key={c.category_id} value={c.category_id} data-testid={`category-option-${c.category_id}`}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {ai && (
          <p className={`rounded-lg px-3 py-2 text-xs ${ai.category_id ? "bg-[#F4C20D]/10 text-[#8A6A00]" : "bg-[#F4F5F0] text-[#495850]"}`} data-testid="ai-suggestion-result">
            <Sparkles className="mr-1 inline h-3.5 w-3.5" /> {ai.message}{ai.category_id && ` (sicurezza ${Math.round(ai.confidence * 100)}%) — puoi sempre cambiarla.`}
          </p>
        )}
      </div>
      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#E2E5DC] bg-[#FBFBF8] p-3.5 text-sm" data-testid="consent-label">
        <Checkbox checked={f.consent} onCheckedChange={(v) => setF({ ...f, consent: !!v })} className="mt-0.5" data-testid="checkbox-referral-consent" />
        <span>Confermo che la persona segnalata ha dato il consenso a essere contattata. *</span>
      </label>
      <Button type="submit" disabled={saving} className="h-12 w-full rounded-full bg-[#F4C20D] text-base text-[#0F2D5E] hover:bg-[#E0B000]" data-testid="referral-submit-btn">
        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Invia segnalazione
      </Button>
    </form>
  );
};
